import './Login.css';

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { APP_VERSION } from '../utils/version';
import GoogleSignInIcon from '../components/GoogleSignInIcon';
import { getGoogleAuthStartUrl } from '../utils/googleAuthUrl';
import {
  parseLoginErrorParam,
  readLoginErrorFromWindow,
  LOGIN_ERROR_STORAGE_KEY
} from '../utils/loginErrorMessage';

function Login() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [loginError, setLoginError] = useState(() => {
    const fromUrl = readLoginErrorFromWindow();
    if (fromUrl) {
      return fromUrl;
    }
    try {
      return sessionStorage.getItem(LOGIN_ERROR_STORAGE_KEY) || '';
    } catch {
      return '';
    }
  });
  const { login, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const oauthCallbackHandled = useRef(false);

  useEffect(() => {
    document.documentElement.classList.add('login-route');
    return () => {
      document.documentElement.classList.remove('login-route');
    };
  }, []);

  useEffect(() => {
    if (oauthCallbackHandled.current) {
      return;
    }

    const error = searchParams.get('error');
    const token = searchParams.get('token');

    if (!error && !token) {
      return;
    }

    oauthCallbackHandled.current = true;
    setSearchParams({}, { replace: true });

    if (error) {
      const message = parseLoginErrorParam(error);
      if (message) {
        setLoginError(message);
        try {
          sessionStorage.removeItem(LOGIN_ERROR_STORAGE_KEY);
        } catch {
          /* ignore */
        }
        toast.error(message);
      }
      return;
    }

    if (token) {
      setGoogleLoading(true);
      loginWithToken(token)
        .then((result) => {
          if (result.success) {
            setLoginError('');
            toast.success('Login successful');
            navigate('/', { replace: true });
          } else {
            const msg = result.error || 'Google Sign-In failed';
            setLoginError(msg);
            toast.error(msg);
          }
        })
        .finally(() => setGoogleLoading(false));
    }
  }, [searchParams, setSearchParams, loginWithToken, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');

    try {
      const result = await login(formData.email, formData.password);

      if (result.success) {
        toast.success('Login successful');
      } else {
        setLoginError(result.error);
        toast.error(result.error);
      }
    } catch (error) {
      console.error('Login exception:', error);
      const msg = 'Login failed';
      setLoginError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const isDisabled = loading || googleLoading;
  const googleAuthUrl = getGoogleAuthStartUrl();

  return (
    <div className="login-container">
      <div className="login-card card">
        <h2 className="text-center login-title">Arches Attendance Management</h2>
        <h3 className="text-center mb-4">Login</h3>
        <div className="text-center mb-2 text-muted small">{APP_VERSION}</div>

        {loginError ? (
          <div className="login-error-alert" role="alert">
            {loginError}
          </div>
        ) : null}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input
              type="email"
              name="email"
              className="form-control"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={isDisabled}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              name="password"
              className="form-control"
              value={formData.password}
              onChange={handleChange}
              required
              disabled={isDisabled}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary login-primary-btn"
            disabled={isDisabled}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className="login-divider" aria-hidden="true">
          <span>or</span>
        </div>

        <a
          href={googleAuthUrl}
          className={`btn btn-google${isDisabled ? ' btn-google--disabled' : ''}`}
          aria-disabled={isDisabled}
          onClick={(e) => {
            if (isDisabled) {
              e.preventDefault();
            }
          }}
        >
          <GoogleSignInIcon size={20} />
          <span>
            {googleLoading
              ? 'Signing in...'
              : 'Sign in with your Arches Global Google account.'}
          </span>
        </a>
      </div>
    </div>
  );
}

export default Login;
